/**
 * Work Details Tab — project-type-aware daily work log.
 * Supports 5 project-type variants: Thermoplastic (family), Signages, Road Studs,
 * Crash Barrier, Other Road Safety Products. Shows "Material at Site" below.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, Save, X, ClipboardList, Package, Calculator, AlertCircle } from 'lucide-react';
import { siteProjectAPI } from '../../services/api';
import ProductSearchInput from '../ui/ProductSearchInput';

const THERMO_TYPES = ['Thermoplastic Roadmarking', 'Kerb Marking', 'Cold Plastic'];
const fmt = (n) => (Number(n) || 0).toLocaleString('en-IN', { maximumFractionDigits: 3 });

const emptyForm = (projectType) => ({
  date: new Date().toISOString().slice(0, 10),
  project_type: projectType,
  quantity: 0,
  quantity_unit: '',
  labour_charge: 0,
  remarks: '',
  thermoplastic_product_name: '', thermoplastic_qty: 0, thermoplastic_unit: 'kg',
  glass_beads_product_name: '', glass_beads_qty: 0, glass_beads_unit: 'kg',
  primer_product_name: '', primer_qty: 0, primer_unit: 'kg',
  // Extras allow capturing multiple products of the same category on a single work day
  extra_thermoplastic_items: [],
  extra_glass_beads_items: [],
  extra_primer_items: [],
  signage_shape: '', signage_size: '', signage_name: '', signage_product_name: '',
  roadstud_product_name: '', epoxy_product_name: '', epoxy_qty: 0, epoxy_unit: 'kg',
  crash_barrier_product_name: '',
  item_name: '', item_product_name: '', other_items: [],
});

// -------- Thermoplastic auto-calc (client-side preview; server recomputes on save) --------
const asKg = (q, u) => ((u || '').toLowerCase() === 'bags' ? (Number(q) || 0) * 25 : (Number(q) || 0));
const sumExtras = (rows) => (rows || []).reduce((s, r) => s + asKg(r.qty, r.unit), 0);
const computeThermoPreview = (f) => {
  const t_kg = asKg(f.thermoplastic_qty, f.thermoplastic_unit) + sumExtras(f.extra_thermoplastic_items);
  const g_kg = asKg(f.glass_beads_qty, f.glass_beads_unit) + sumExtras(f.extra_glass_beads_items);
  const p_kg = asKg(f.primer_qty, f.primer_unit) + sumExtras(f.extra_primer_items);
  const total_kg = t_kg + g_kg + p_kg;
  const area = Number(f.quantity) || 0;
  const avg_t = area > 0 ? t_kg / area : 0;
  const avg_g = area > 0 ? g_kg / area : 0;
  return { total_kg, avg_t, avg_g };
};

// Helper: render a row of extra material (product search + qty + unit + delete)
const ExtraMaterialRow = ({ row, units, onChange, onRemove, testPrefix }) => (
  <div className="grid grid-cols-1 md:grid-cols-[1fr_120px_140px_36px] gap-2 items-center">
    <ProductSearchInput
      value={row.product_name || ''}
      onChange={(v) => onChange({ ...row, product_name: v })}
      onSelect={(p) => onChange({ ...row, product_name: p.name || '', unit: row.unit || (p.unit || 'kg') })}
      onClear={() => onChange({ ...row, product_name: '' })}
      placeholder="Search product…"
      testId={`${testPrefix}-search`}
      showMeta={false}
    />
    <input type="number" min="0" placeholder="Qty" value={row.qty || ''}
      onChange={(e) => onChange({ ...row, qty: Math.max(0, parseFloat(e.target.value) || 0) })}
      className="px-3 py-2 text-xs border border-gray-200 rounded-lg"
      data-testid={`${testPrefix}-qty`} />
    <select value={row.unit || 'kg'} onChange={(e) => onChange({ ...row, unit: e.target.value })}
      className="px-3 py-2 text-xs border border-gray-200 rounded-lg" data-testid={`${testPrefix}-unit`}>
      {units.map(u => (<option key={u.value} value={u.value}>{u.label}</option>))}
    </select>
    <button type="button" onClick={onRemove} className="p-2 rounded-lg hover:bg-red-50" data-testid={`${testPrefix}-remove`}>
      <Trash2 className="w-3.5 h-3.5 text-red-500" />
    </button>
  </div>
);

// -------- Type-specific form bodies --------
const ThermoplasticForm = ({ form, setForm, preview, avgRef }) => {
  const thermoUnitMatches = form.thermoplastic_unit && form.glass_beads_qty > 0 && form.thermoplastic_unit !== form.glass_beads_unit;
  const TP_UNITS = [{ value: 'kg', label: 'kg' }, { value: 'bags', label: 'bags (25 kg each)' }];
  const PRIMER_UNITS = [{ value: 'kg', label: 'kg' }, { value: 'ltr', label: 'ltr' }, { value: 'bags', label: 'bags (25 kg each)' }];

  const addExtra = (key) => setForm({ ...form, [key]: [...(form[key] || []), { product_name: '', qty: 0, unit: 'kg' }] });
  const updateExtra = (key, idx, row) => setForm({ ...form, [key]: form[key].map((r, i) => i === idx ? row : r) });
  const removeExtra = (key, idx) => setForm({ ...form, [key]: form[key].filter((_, i) => i !== idx) });

  return (
    <div className="space-y-3">
      <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Thermoplastic Compound</p>
      <div className="grid grid-cols-1 md:grid-cols-[1fr_120px_140px_36px] gap-2 items-center">
        <ProductSearchInput
          value={form.thermoplastic_product_name}
          onChange={(v) => setForm({ ...form, thermoplastic_product_name: v })}
          onSelect={(p) => setForm({ ...form, thermoplastic_product_name: p.name || '', thermoplastic_unit: form.thermoplastic_unit || (p.unit || 'kg') })}
          onClear={() => setForm({ ...form, thermoplastic_product_name: '' })}
          placeholder="Search product (e.g. TP White)…"
          testId="wd-thermo-search"
          showMeta={false}
        />
        <input data-testid="wd-thermo-qty" type="number" min="0" placeholder="Qty" value={form.thermoplastic_qty || ''} onChange={e => setForm({ ...form, thermoplastic_qty: Math.max(0, parseFloat(e.target.value) || 0) })} className="px-3 py-2 text-xs border border-gray-200 rounded-lg" />
        <select data-testid="wd-thermo-unit" value={form.thermoplastic_unit} onChange={e => setForm({ ...form, thermoplastic_unit: e.target.value })} className="px-3 py-2 text-xs border border-gray-200 rounded-lg">
          <option value="kg">kg</option><option value="bags">bags (25 kg each)</option>
        </select>
        <span></span>
      </div>
      {(form.extra_thermoplastic_items || []).map((row, i) => (
        <ExtraMaterialRow key={`et-${i}`} row={row} units={TP_UNITS}
          onChange={(r) => updateExtra('extra_thermoplastic_items', i, r)}
          onRemove={() => removeExtra('extra_thermoplastic_items', i)}
          testPrefix={`wd-thermo-extra-${i}`} />
      ))}
      <button type="button" onClick={() => addExtra('extra_thermoplastic_items')}
        data-testid="wd-thermo-add-extra"
        className="text-[11px] font-semibold text-blue-600 hover:underline flex items-center gap-1">
        <Plus className="w-3 h-3" /> Add another Thermoplastic
      </button>

      <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider pt-1">Glass Beads</p>
      <div className="grid grid-cols-1 md:grid-cols-[1fr_120px_140px_36px] gap-2 items-center">
        <ProductSearchInput
          value={form.glass_beads_product_name}
          onChange={(v) => setForm({ ...form, glass_beads_product_name: v })}
          onSelect={(p) => setForm({ ...form, glass_beads_product_name: p.name || '', glass_beads_unit: form.glass_beads_unit || (p.unit || 'kg') })}
          onClear={() => setForm({ ...form, glass_beads_product_name: '' })}
          placeholder="Search product…"
          testId="wd-beads-search"
          showMeta={false}
        />
        <input data-testid="wd-beads-qty" type="number" min="0" placeholder="Qty" value={form.glass_beads_qty || ''} onChange={e => setForm({ ...form, glass_beads_qty: Math.max(0, parseFloat(e.target.value) || 0) })} className="px-3 py-2 text-xs border border-gray-200 rounded-lg" />
        <select data-testid="wd-beads-unit" value={form.glass_beads_unit} onChange={e => setForm({ ...form, glass_beads_unit: e.target.value })} className="px-3 py-2 text-xs border border-gray-200 rounded-lg">
          <option value="kg">kg</option><option value="bags">bags (25 kg each)</option>
        </select>
        <span></span>
      </div>
      {(form.extra_glass_beads_items || []).map((row, i) => (
        <ExtraMaterialRow key={`eg-${i}`} row={row} units={TP_UNITS}
          onChange={(r) => updateExtra('extra_glass_beads_items', i, r)}
          onRemove={() => removeExtra('extra_glass_beads_items', i)}
          testPrefix={`wd-beads-extra-${i}`} />
      ))}
      <button type="button" onClick={() => addExtra('extra_glass_beads_items')}
        data-testid="wd-beads-add-extra"
        className="text-[11px] font-semibold text-blue-600 hover:underline flex items-center gap-1">
        <Plus className="w-3 h-3" /> Add another Glass Beads
      </button>

      <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider pt-1">Primer <span className="text-[9px] text-gray-400 font-normal normal-case">(optional)</span></p>
      <div className="grid grid-cols-1 md:grid-cols-[1fr_120px_140px_36px] gap-2 items-center">
        <ProductSearchInput
          value={form.primer_product_name}
          onChange={(v) => setForm({ ...form, primer_product_name: v })}
          onSelect={(p) => setForm({ ...form, primer_product_name: p.name || '', primer_unit: form.primer_unit || (p.unit || 'kg') })}
          onClear={() => setForm({ ...form, primer_product_name: '' })}
          placeholder="Search product…"
          testId="wd-primer-search"
          showMeta={false}
        />
        <input data-testid="wd-primer-qty" type="number" min="0" placeholder="Qty" value={form.primer_qty || ''} onChange={e => setForm({ ...form, primer_qty: Math.max(0, parseFloat(e.target.value) || 0) })} className="px-3 py-2 text-xs border border-gray-200 rounded-lg" />
        <select value={form.primer_unit} onChange={e => setForm({ ...form, primer_unit: e.target.value })} className="px-3 py-2 text-xs border border-gray-200 rounded-lg">
          <option value="kg">kg</option><option value="ltr">ltr</option><option value="bags">bags (25 kg each)</option>
        </select>
        <span></span>
      </div>
      {(form.extra_primer_items || []).map((row, i) => (
        <ExtraMaterialRow key={`ep-${i}`} row={row} units={PRIMER_UNITS}
          onChange={(r) => updateExtra('extra_primer_items', i, r)}
          onRemove={() => removeExtra('extra_primer_items', i)}
          testPrefix={`wd-primer-extra-${i}`} />
      ))}
      <button type="button" onClick={() => addExtra('extra_primer_items')}
        data-testid="wd-primer-add-extra"
        className="text-[11px] font-semibold text-blue-600 hover:underline flex items-center gap-1">
        <Plus className="w-3 h-3" /> Add another Primer
      </button>

      {thermoUnitMatches && (
        <div className="flex items-start gap-2 p-2 bg-amber-50 border border-amber-200 rounded-lg">
          <AlertCircle className="w-3.5 h-3.5 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-[11px] text-amber-700">Thermoplastic and Glass Beads have different units ({form.thermoplastic_unit} vs {form.glass_beads_unit}). Total kg will still be computed correctly using the ×25 rule for bags.</p>
        </div>
      )}
      {/* Live Auto-calc preview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 p-3 bg-indigo-50 rounded-lg">
        <div><p className="text-[10px] text-indigo-500 uppercase">Total Material</p><p className="text-sm font-bold text-indigo-700" data-testid="wd-total-kg">{fmt(preview.total_kg)} kg</p></div>
        <div><p className="text-[10px] text-indigo-500 uppercase">Avg Thermoplastic</p><p className="text-sm font-bold text-indigo-700">{fmt(preview.avg_t)} kg/sqm</p></div>
        <div><p className="text-[10px] text-indigo-500 uppercase">Avg Glass Beads</p><p className="text-sm font-bold text-indigo-700">{fmt(preview.avg_g)} kg/sqm</p></div>
      </div>
      {avgRef > 0 && preview.avg_t > 0 && (
        <p className={`text-xs font-semibold ${preview.avg_t > avgRef ? 'text-red-600' : 'text-emerald-600'}`} data-testid="wd-avg-remark">
          {preview.avg_t > avgRef ? `⚠ Above reference avg (${avgRef} kg/sqm)` : `✓ Below reference avg (${avgRef} kg/sqm)`}
        </p>
      )}
    </div>
  );
};

const SignagesForm = ({ form, setForm }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
    <input data-testid="wd-sig-shape" placeholder="Signboard Shape (e.g. Circular)" value={form.signage_shape} onChange={e => setForm({ ...form, signage_shape: e.target.value })} className="px-3 py-2 text-xs border border-gray-200 rounded-lg" />
    <input data-testid="wd-sig-size" placeholder="Size (e.g. 900mm)" value={form.signage_size} onChange={e => setForm({ ...form, signage_size: e.target.value })} className="px-3 py-2 text-xs border border-gray-200 rounded-lg" />
    <input data-testid="wd-sig-product" placeholder="Product name (SKU / board)" value={form.signage_product_name} onChange={e => setForm({ ...form, signage_product_name: e.target.value })} className="px-3 py-2 text-xs border border-gray-200 rounded-lg" />
    <input data-testid="wd-sig-name" placeholder="Signboard name (optional)" value={form.signage_name} onChange={e => setForm({ ...form, signage_name: e.target.value })} className="px-3 py-2 text-xs border border-gray-200 rounded-lg" />
  </div>
);

const RoadStudsForm = ({ form, setForm }) => (
  <div className="space-y-2">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
      <input data-testid="wd-stud-product" placeholder="Road stud product name" value={form.roadstud_product_name} onChange={e => setForm({ ...form, roadstud_product_name: e.target.value })} className="px-3 py-2 text-xs border border-gray-200 rounded-lg" />
      <input data-testid="wd-epoxy-product" placeholder="Epoxy product name" value={form.epoxy_product_name} onChange={e => setForm({ ...form, epoxy_product_name: e.target.value })} className="px-3 py-2 text-xs border border-gray-200 rounded-lg" />
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
      <input data-testid="wd-epoxy-qty" type="number" min="0" placeholder="Epoxy qty" value={form.epoxy_qty || ''} onChange={e => setForm({ ...form, epoxy_qty: Math.max(0, parseFloat(e.target.value) || 0) })} className="px-3 py-2 text-xs border border-gray-200 rounded-lg" />
      <select value={form.epoxy_unit} onChange={e => setForm({ ...form, epoxy_unit: e.target.value })} className="px-3 py-2 text-xs border border-gray-200 rounded-lg">
        <option value="kg">kg</option><option value="ltr">ltr</option><option value="pkt">pkt</option>
      </select>
    </div>
  </div>
);

const CrashBarrierForm = ({ form, setForm }) => (
  <input data-testid="wd-crash-product" placeholder="Crash barrier product name" value={form.crash_barrier_product_name} onChange={e => setForm({ ...form, crash_barrier_product_name: e.target.value })} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg" />
);

const OtherProductsForm = ({ form, setForm }) => {
  const addOther = () => setForm({ ...form, other_items: [...(form.other_items || []), { product_name: '', qty: 0, unit: '' }] });
  const updOther = (i, k, v) => { const o = [...(form.other_items || [])]; o[i] = { ...o[i], [k]: v }; setForm({ ...form, other_items: o }); };
  const delOther = (i) => { const o = [...(form.other_items || [])]; o.splice(i, 1); setForm({ ...form, other_items: o }); };
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <input data-testid="wd-other-name" placeholder="Item name (e.g. Speed Breaker)" value={form.item_name} onChange={e => setForm({ ...form, item_name: e.target.value })} className="px-3 py-2 text-xs border border-gray-200 rounded-lg" />
        <input data-testid="wd-other-product" placeholder="Product name (for procurement match)" value={form.item_product_name} onChange={e => setForm({ ...form, item_product_name: e.target.value })} className="px-3 py-2 text-xs border border-gray-200 rounded-lg" />
      </div>
      <div>
        <div className="flex items-center justify-between mb-1">
          <p className="text-[10px] font-bold text-gray-500 uppercase">Required Items <span className="text-[9px] font-normal normal-case text-gray-400">(e.g. epoxy to stick speed breaker)</span></p>
          <button data-testid="wd-other-add-item" onClick={addOther} className="text-[10px] text-blue-600 hover:underline">+ Add item</button>
        </div>
        {(form.other_items || []).map((oi, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 mb-1">
            <input placeholder="Product name" value={oi.product_name} onChange={e => updOther(i, 'product_name', e.target.value)} className="col-span-6 px-2 py-1.5 text-xs border border-gray-200 rounded" />
            <input type="number" min="0" placeholder="Qty" value={oi.qty || ''} onChange={e => updOther(i, 'qty', Math.max(0, parseFloat(e.target.value) || 0))} className="col-span-3 px-2 py-1.5 text-xs border border-gray-200 rounded" />
            <input placeholder="Unit" value={oi.unit} onChange={e => updOther(i, 'unit', e.target.value)} className="col-span-2 px-2 py-1.5 text-xs border border-gray-200 rounded" />
            <button onClick={() => delOther(i)} className="col-span-1 flex items-center justify-center text-red-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
      </div>
    </div>
  );
};

// -------- Main Form Dialog --------
const WorkDetailDialog = ({ project, editing, onClose, onSaved }) => {
  const isThermo = THERMO_TYPES.includes(project.project_type);
  const [form, setForm] = useState(() => editing ? { ...editing } : { ...emptyForm(project.project_type), quantity_unit: unitForType(project.project_type) });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const preview = isThermo ? computeThermoPreview(form) : { total_kg: 0, avg_t: 0, avg_g: 0 };
  const labourAuto = (Number(project.labour_rate_per_unit) || 0) * (Number(form.quantity) || 0);

  const handleSave = async () => {
    setError('');
    if (!form.date) { setError('Date is required'); return; }
    if (!form.quantity || form.quantity <= 0) { setError('Quantity must be greater than 0'); return; }
    setSaving(true);
    try {
      // Snapshot project_type
      const payload = { ...form, project_type: project.project_type };
      // If user didn't override labour charge, send 0 so server auto-calcs
      if (editing) {
        await siteProjectAPI.updateWorkDetail(project.id, editing.id, payload);
      } else {
        await siteProjectAPI.addWorkDetail(project.id, payload);
      }
      onSaved();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <div>
            <h3 className="text-sm font-bold text-gray-900">{editing ? 'Edit' : 'Add'} Work Detail</h3>
            <p className="text-[11px] text-gray-400">{project.project_type}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-4 space-y-4">
          {/* Common fields */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <div>
              <label className="block text-[10px] text-gray-500 mb-1">Date *</label>
              <input data-testid="wd-date" type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg" />
            </div>
            <div>
              <label className="block text-[10px] text-gray-500 mb-1">Quantity * {form.quantity_unit && <span className="text-gray-400">({form.quantity_unit})</span>}</label>
              <input data-testid="wd-quantity" type="number" min="0" value={form.quantity || ''} onChange={e => setForm({ ...form, quantity: Math.max(0, parseFloat(e.target.value) || 0) })} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg" />
            </div>
            <div>
              <label className="block text-[10px] text-gray-500 mb-1">Unit</label>
              <input data-testid="wd-quantity-unit" value={form.quantity_unit} onChange={e => setForm({ ...form, quantity_unit: e.target.value })} placeholder={unitForType(project.project_type)} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg" />
            </div>
          </div>

          {/* Type-specific body */}
          {isThermo && <ThermoplasticForm form={form} setForm={setForm} preview={preview} avgRef={Number(project.avg_material_consumption_per_unit) || 0} />}
          {project.project_type === 'Signages' && <SignagesForm form={form} setForm={setForm} />}
          {project.project_type === 'Road Studs' && <RoadStudsForm form={form} setForm={setForm} />}
          {project.project_type === 'Crash Barrier' && <CrashBarrierForm form={form} setForm={setForm} />}
          {project.project_type === 'Other Road Safety Products' && <OtherProductsForm form={form} setForm={setForm} />}

          {/* Labour charge */}
          <div className="p-3 border border-emerald-100 bg-emerald-50 rounded-lg">
            <div className="flex items-center justify-between mb-1">
              <label className="text-[10px] font-bold text-emerald-600 uppercase">Labour Charge</label>
              {labourAuto > 0 && (Number(form.labour_charge) || 0) === 0 && (
                <span className="text-[10px] text-emerald-600">Auto: ₹{fmt(labourAuto)} (rate × qty)</span>
              )}
            </div>
            <input data-testid="wd-labour-charge" type="number" min="0" value={form.labour_charge || ''} onChange={e => setForm({ ...form, labour_charge: Math.max(0, parseFloat(e.target.value) || 0) })} placeholder={labourAuto > 0 ? `Leave blank for auto (₹${fmt(labourAuto)})` : 'Enter labour charge'} className="w-full px-3 py-2 text-xs border border-emerald-200 rounded-lg bg-white" />
          </div>

          <div>
            <label className="block text-[10px] text-gray-500 mb-1">Remarks</label>
            <textarea data-testid="wd-remarks" value={form.remarks} onChange={e => setForm({ ...form, remarks: e.target.value })} rows={2} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg" />
          </div>

          {error && <div className="p-2 bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg">{error}</div>}
        </div>
        <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-100">
          <button onClick={onClose} className="px-4 py-2 text-xs text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
          <button data-testid="wd-save-btn" onClick={handleSave} disabled={saving} className="px-4 py-2 text-xs text-white rounded-lg disabled:opacity-50" style={{ backgroundColor: '#24549c' }}>
            <Save className="w-3.5 h-3.5 inline mr-1" />{saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

const unitForType = (t) => {
  switch (t) {
    case 'Thermoplastic Roadmarking':
    case 'Kerb Marking':
    case 'Cold Plastic': return 'sqm';
    case 'Crash Barrier': return 'mtr';
    case 'Signages':
    case 'Road Studs':
    case 'Other Road Safety Products': return 'pcs';
    default: return '';
  }
};

// -------- Material at Site subsection --------
const MaterialAtSite = ({ projectId, refreshKey }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await siteProjectAPI.getMaterialAtSite(projectId);
      setData(r);
    } finally {
      setLoading(false);
    }
  }, [projectId]);
  useEffect(() => { load(); }, [load, refreshKey]);

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-4 mt-4" data-testid="material-at-site">
      <div className="flex items-center gap-2 mb-3">
        <Package className="w-4 h-4 text-indigo-600" />
        <h4 className="text-sm font-bold text-gray-900">Material at Site</h4>
        <span className="text-[10px] text-gray-400">= Procurement − Work Done</span>
      </div>
      {loading ? <p className="text-xs text-gray-400">Loading…</p> : (
        (data?.rows || []).length === 0 ? (
          <p className="text-xs text-gray-400">No procurement or consumption recorded yet.</p>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[10px] text-gray-400 uppercase border-b border-gray-100">
                <th className="text-left py-2">Product</th>
                <th className="text-right py-2">Procured</th>
                <th className="text-right py-2">Consumed</th>
                <th className="text-right py-2">Remaining</th>
                <th className="text-right py-2">Unit</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r, i) => (
                <tr key={i} className="border-b border-gray-50">
                  <td className="py-2 font-medium text-gray-700">{r.product_name}</td>
                  <td className="py-2 text-right text-gray-600">{fmt(r.procured)}</td>
                  <td className="py-2 text-right text-red-600">{fmt(r.consumed)}</td>
                  <td className={`py-2 text-right font-bold ${r.remaining < 0 ? 'text-red-600' : 'text-emerald-600'}`}>{fmt(r.remaining)}</td>
                  <td className="py-2 text-right text-gray-400">{r.unit}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      )}
    </div>
  );
};

// -------- Main Tab --------
export const WorkDetailsTab = ({ project }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await siteProjectAPI.listWorkDetails(project.id);
      setEntries(list || []);
    } finally {
      setLoading(false);
    }
  }, [project.id]);
  useEffect(() => { load(); }, [load]);

  const handleDelete = async (wdId) => {
    if (!window.confirm('Delete this work entry?')) return;
    await siteProjectAPI.deleteWorkDetail(project.id, wdId);
    setRefreshKey(k => k + 1);
    load();
  };

  const onSaved = () => { setDialogOpen(false); setEditing(null); setRefreshKey(k => k + 1); load(); };

  const isThermo = THERMO_TYPES.includes(project.project_type);
  const totalLabour = entries.reduce((s, e) => s + (Number(e.labour_charge) || 0), 0);
  const totalQty = entries.reduce((s, e) => s + (Number(e.quantity) || 0), 0);

  if (!project.project_type) {
    return <div className="text-center py-12 text-gray-400"><AlertCircle className="w-8 h-8 mx-auto mb-2 text-gray-300" /><p className="text-sm">Set a Project Type in Overview to enable Work Details.</p></div>;
  }

  return (
    <div className="space-y-4" data-testid="work-details-tab">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-sm font-bold text-gray-900 flex items-center gap-2"><ClipboardList className="w-4 h-4" /> Work Details</h4>
          <p className="text-[11px] text-gray-400">Daily work log for {project.project_type}</p>
        </div>
        <button data-testid="wd-add-btn" onClick={() => { setEditing(null); setDialogOpen(true); }} className="px-3 py-1.5 text-xs text-white rounded-lg flex items-center gap-1" style={{ backgroundColor: '#24549c' }}>
          <Plus className="w-3.5 h-3.5" /> Add Work
        </button>
      </div>

      {/* Summary tiles */}
      {entries.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          <div className="p-2 bg-blue-50 rounded-lg"><p className="text-[10px] text-blue-500 uppercase">Entries</p><p className="text-sm font-bold text-blue-700">{entries.length}</p></div>
          <div className="p-2 bg-emerald-50 rounded-lg"><p className="text-[10px] text-emerald-600 uppercase">Total Qty</p><p className="text-sm font-bold text-emerald-700">{fmt(totalQty)} {unitForType(project.project_type)}</p></div>
          <div className="p-2 bg-amber-50 rounded-lg"><p className="text-[10px] text-amber-600 uppercase">Total Labour</p><p className="text-sm font-bold text-amber-700">₹{fmt(totalLabour)}</p></div>
        </div>
      )}

      {/* Entries list */}
      {loading ? <p className="text-xs text-gray-400">Loading…</p> : entries.length === 0 ? (
        <div className="text-center py-8 text-gray-400">
          <Calculator className="w-8 h-8 mx-auto mb-2 text-gray-300" />
          <p className="text-sm">No work entries yet. Click "Add Work" to log today's activity.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 overflow-x-auto">
          <table className="w-full text-xs min-w-[680px]">
            <thead>
              <tr className="bg-gray-50 text-[10px] text-gray-500 uppercase">
                <th className="text-left py-2 px-3">Date</th>
                <th className="text-left py-2 px-3">Details</th>
                <th className="text-right py-2 px-3">Qty</th>
                {isThermo && <th className="text-right py-2 px-3">Total Kg</th>}
                {isThermo && <th className="text-right py-2 px-3">Avg T/G</th>}
                <th className="text-right py-2 px-3">Labour</th>
                <th className="text-right py-2 px-3 w-24">Actions</th>
              </tr>
            </thead>
            <tbody>
              {entries.map(e => (
                <tr key={e.id} data-testid={`wd-row-${e.id}`} className="border-b border-gray-50 hover:bg-gray-50">
                  <td className="py-2 px-3 text-gray-600">
                    <div>{e.date}</div>
                    {/* Service Invoice badge */}
                    {(() => {
                      const st = e.service_status || 'Pending';
                      if (st === 'Invoiced') return <span className="inline-block mt-0.5 text-[9px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-semibold mr-1" data-testid={`wd-status-invoiced-${e.id}`}>✓ Svc Invoiced</span>;
                      if (st === 'Partially Invoiced') return <span className="inline-block mt-0.5 text-[9px] px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 font-semibold mr-1" data-testid={`wd-status-partial-${e.id}`}>◐ Svc {fmt(e.service_invoiced_qty)}/{fmt(e.quantity)}</span>;
                      return <span className="inline-block mt-0.5 text-[9px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-semibold mr-1" data-testid={`wd-status-pending-${e.id}`}>⏳ Svc Pending</span>;
                    })()}
                    {/* Labour Invoice badge */}
                    {(() => {
                      const st = e.labour_status || 'Pending';
                      if (st === 'Invoiced') return <span className="inline-block mt-0.5 text-[9px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-semibold" data-testid={`wd-lab-invoiced-${e.id}`}>⚙ Lab Invoiced</span>;
                      if (st === 'Partially Invoiced') return <span className="inline-block mt-0.5 text-[9px] px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 font-semibold" data-testid={`wd-lab-partial-${e.id}`}>◐ Lab {fmt(e.labour_invoiced_qty)}/{fmt(e.quantity)}</span>;
                      return null;
                    })()}
                  </td>
                  <td className="py-2 px-3">
                    {isThermo && (
                      <div>
                        <span className="font-medium text-gray-700">{e.thermoplastic_product_name || '-'}</span>
                        <span className="text-gray-400"> + </span>
                        <span className="font-medium text-gray-700">{e.glass_beads_product_name || '-'}</span>
                        {e.avg_remark && <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded ${e.avg_remark.includes('Above') ? 'bg-red-50 text-red-600' : 'bg-emerald-50 text-emerald-600'}`}>{e.avg_remark}</span>}
                      </div>
                    )}
                    {project.project_type === 'Signages' && <span className="text-gray-700">{e.signage_shape} {e.signage_size} {e.signage_name && <span className="text-gray-400">— {e.signage_name}</span>}</span>}
                    {project.project_type === 'Road Studs' && <span className="text-gray-700">{e.roadstud_product_name} {e.epoxy_product_name && <span className="text-gray-400">+ {e.epoxy_product_name} ({fmt(e.epoxy_qty)} {e.epoxy_unit})</span>}</span>}
                    {project.project_type === 'Crash Barrier' && <span className="text-gray-700">{e.crash_barrier_product_name}</span>}
                    {project.project_type === 'Other Road Safety Products' && <span className="text-gray-700">{e.item_name} {e.item_product_name && <span className="text-gray-400">— {e.item_product_name}</span>}</span>}
                    {e.remarks && <p className="text-[10px] text-gray-400 mt-0.5">{e.remarks}</p>}
                  </td>
                  <td className="py-2 px-3 text-right font-semibold">{fmt(e.quantity)} <span className="text-[10px] text-gray-400">{e.quantity_unit}</span></td>
                  {isThermo && <td className="py-2 px-3 text-right text-indigo-700 font-semibold">{fmt(e.total_material_kg)}</td>}
                  {isThermo && <td className="py-2 px-3 text-right text-[11px]">{fmt(e.avg_thermoplastic)}/{fmt(e.avg_glass_beads)}</td>}
                  <td className="py-2 px-3 text-right font-bold text-emerald-700">₹{fmt(e.labour_charge)}</td>
                  <td className="py-2 px-3 text-right">
                    {((e.service_allocations && e.service_allocations.length > 0) || (e.labour_allocations && e.labour_allocations.length > 0) || e.service_invoice_id) ? (
                      <span className="text-[10px] text-gray-400" data-testid={`wd-locked-${e.id}`}>Locked</span>
                    ) : (
                      <>
                        <button data-testid={`wd-edit-${e.id}`} onClick={() => { setEditing(e); setDialogOpen(true); }} className="text-blue-600 hover:underline text-[11px] mr-2">Edit</button>
                        <button data-testid={`wd-delete-${e.id}`} onClick={() => handleDelete(e.id)} className="text-red-500 hover:underline text-[11px]">Delete</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Material at Site */}
      <MaterialAtSite projectId={project.id} refreshKey={refreshKey} />

      {dialogOpen && <WorkDetailDialog project={project} editing={editing} onClose={() => { setDialogOpen(false); setEditing(null); }} onSaved={onSaved} />}
    </div>
  );
};

export default WorkDetailsTab;
